import React, { useContext, useState } from "react";
import {
    GelForm,
    GelFormField,
    useGelFormData,
    GelButton,
    GelBoxLayout,
    GelScreenDetectorContext,
    GelContainerLite,
    GelIcon,
    GelLabel,
    GelHeading3,
    GelParagraph,
    GelRowLayout,
    GelSpinner,
    GelPhoneNumberInput,
    GelTextInput,
    GelLink
} from "@tal-gel/components";
import { getGelTokens } from "@tal-gel/theming";
import FundBanner from "../../../components/fundBanner";
import {
    CorroLinkGraphQL,
    RESEND_CORRO_LINK_VERIFICATION_MUTATION,
    SELECT_CUSTOMER_MUTATION,
    VERIFY_SMS_CORRO_LINK_MUTATION,
} from "../../../graphql/mutations/graphql-corrolink";
import {
    CorroLinkFileResult,
    ResendCorroLinkSms,
    SelectCustomerCorroLink,
    VerifyCorroLink,
} from "../CorroLinkModels";
import useCountdown from "../../../common/hooks/useCountdown";
import { useGetHomePageContent } from "../../../common/api/graphQLDataFetch";
import { interpolateString } from "../../../common/helpers/helpers";

interface VerifyCorroLinkIdentityProps {
    trackingId: string;
    sessionId: string;
    setFileResults: (result: CorroLinkFileResult) => void;
}

type Stage = "enterMobile" | "enterCode";
type SelectCustomerVars = {
    trackingId: string;
    sessionId: string;
    recipient: string
};
type VerifySmsVars = {
    trackingId: string;
    sessionId: string;
    customerId: string;
    passCode: string;
};
type ResendOtpVars = {
    trackingId: string;
    sessionId: string;
    customerId: string
};

const VerifyCorroLinkIdentity: React.FC<VerifyCorroLinkIdentityProps> = ({
    trackingId,
    sessionId,
    setFileResults: setfFileResults
}) => {
    /* ── design tokens / screen helpers ─────────────────────────────── */
    const {
        global: { sizeBaseUnit, sizeBaseX2, sizeNone, themeColorTextDanger },
    } = getGelTokens();
    const {
        screen,
        isXsScreen,
        isLargerThanSmScreen,
    } = useContext(GelScreenDetectorContext) as GelScreenDetectorContext;

    /* ── state -------------------------------------------------------- */
    const [stage, setStage] = useState<Stage>("enterMobile");
    const [statusMsg, setStatusMsg] = useState("");
    const [loading, setLoading] = useState(false);
    const [customerId, setCustomerId] = useState("");
    const [recipient, setRecipient] = useState("");
    const [buttonLoading, isButtonLoading] = useState(false);
    const { remaining, start } = useCountdown(30);
    const [otpResendCount, setOtpResendCount] = useState(0);
    const [otpResendClicked, setOtpResendClicked] = useState(false);

    /* ── form hooks --------------------------------------------------- */
    const contactForm = useGelFormData({ mobile: "" });
    const codeForm = useGelFormData({ verificationCode: "" });

    /* ── helpers ------------------------------------------------------ */
    const commonFormProps = {
        labelAtTop: true,
        width: isXsScreen(screen) ? "100%" : sizeBaseUnit * 110,
        fieldGutter: sizeBaseUnit * 10,
        ...(isXsScreen(screen) ? {} : { labelWidth: sizeBaseUnit * 50 }),
        parseResponseBody: false,
        style: { paddingRight: sizeBaseUnit * 16, paddingLeft: sizeBaseUnit * 16 },
    } as const;

    const handleGraphQlError = (msg: string) => {
        setLoading(false);
        setStatusMsg(msg);
    };

    /* ── actions ------------------------------------------------------ */
    const selectCustomer = async () => {
        setLoading(true);
        setStatusMsg("");
        try {
            var variables: SelectCustomerVars = {
                trackingId,
                sessionId,
                recipient: contactForm.formData.mobile,
            };
            const resp = await CorroLinkGraphQL<SelectCustomerVars, SelectCustomerCorroLink>(
                SELECT_CUSTOMER_MUTATION, variables);

            if (resp.status !== "success") 
                return handleGraphQlError(data?.corroLink?.Verify?.Mobile?.Error?.Failed ?? "");

            setCustomerId(resp.customerId);
            setRecipient(resp.recipient);
            start(); //Start OTP countdown timer
            setStage("enterCode");
        } catch {
            handleGraphQlError(data?.corroLink?.Verify?.Mobile?.Error?.Failed ?? "");
        } finally {
            setLoading(false);
        }
    };

    const verifyCustomer = async () => {
        setLoading(true);
        setStatusMsg("");
        try {
            const variables: VerifySmsVars = {
                    trackingId,
                    sessionId,
                    customerId,
                    passCode: codeForm.formData.verificationCode
                };

            const resp = await CorroLinkGraphQL<VerifySmsVars, VerifyCorroLink>(
                VERIFY_SMS_CORRO_LINK_MUTATION,
                variables
            );

            if (resp.status !== "success") {
                if (resp.status === "verificationFailed") {
                    return handleGraphQlError(data?.corroLink?.Verify?.OTP?.Error?.Invalid ?? "");
                }
                else {
                    return handleGraphQlError(data?.corroLink?.Verify?.OTP?.Error?.Failed ?? "");
                }
            }

            setfFileResults({
                attachments: resp.attachments,
                createdAt: resp.createdAt,
                policyDetails: resp.policyDetails,
                policyNumber: resp.policyNumber,
                claimNumber: resp.claimNumber,
                lifeInsured: resp.lifeInsured
            })
        } catch {
            handleGraphQlError(data?.corroLink?.Verify?.OTP?.Error?.Failed ?? "");
        } finally {
            setLoading(false);
        }
    };

    const resendCode = () => {
        setOtpResendClicked(true);
        if (remaining > 0) return;

        isButtonLoading(true);
        setStatusMsg("");
        const variables: ResendOtpVars = { trackingId, sessionId, customerId };
        CorroLinkGraphQL<ResendOtpVars, ResendCorroLinkSms>(
            RESEND_CORRO_LINK_VERIFICATION_MUTATION, variables)
            .then((resp) => {
                isButtonLoading(false);
                if (resp.status !== "success") {
                    handleGraphQlError(data?.corroLink?.Verify?.OTP?.Resend?.Failed ?? "");
                } else {
                    start(); // reset countdown timer
                    setOtpResendCount(c => c + 1);
                }
            });
    }

    const ErrorBox = () =>
        <>
            {statusMsg ? (
                <GelBoxLayout space={[1, 8]}>
                    <GelIcon color={themeColorTextDanger} name="AlertCircle" inline />
                    <GelLabel style={{ color: themeColorTextDanger }}>{statusMsg}</GelLabel>
                </GelBoxLayout>
            ) : null}
            {remaining > 0 && otpResendClicked ? (
                <GelBoxLayout space={[1, 8]}>
                    <GelIcon color={themeColorTextDanger} name="AlertCircle" inline />
                    <GelLabel style={{ color: themeColorTextDanger }}>
                        {otpResendCount > 0 ? 
                            interpolateString(data?.corroLink?.Verify?.OTP?.Resend?.SubsequentCoolDown, remaining) :
                            interpolateString(data?.corroLink?.Verify?.OTP?.Resend?.FirstCoolDown, remaining)}
                    </GelLabel>
                </GelBoxLayout>
            ) : null}
        </>
    
    const { data, isLoading } = useGetHomePageContent()    

    /* ── ui ----------------------------------------------------------- */
    if (isLoading || loading) return <GelSpinner medium overlay />;

    return (
        <GelContainerLite
            style={{
                paddingRight: sizeNone,
                paddingLeft: sizeNone,
                paddingBottom: sizeBaseUnit * 15,
                paddingTop: sizeBaseUnit * 15,
                overflow: "hidden",
            }}
        >
            <GelBoxLayout alignment="start" space={[1, 4]} gutter="none">
                {stage === "enterMobile" && (
                    <GelForm
                        {...commonFormProps}
                        disableOnSubmit
                        onSubmit={selectCustomer}
                        reset={contactForm.resetForm}
                        onReset={contactForm.onFormDataReset}
                    >
                        <GelRowLayout gutter="medium" style={{ paddingLeft: sizeBaseUnit * 3 }}>
                            <GelHeading3>
                                {data?.corroLink?.Verify?.Mobile?.Heading}
                            </GelHeading3>
                            <GelParagraph style={{ paddingTop: sizeBaseUnit * 2 }}>
                                {data?.corroLink?.Verify?.Mobile?.SubHeading}
                            </GelParagraph>

                            <GelFormField label="Mobile number">
                                <GelPhoneNumberInput
                                    name="mobile"
                                    onChange={contactForm.onFormDataChange}
                                    value={contactForm.formData.mobile}
                                    required
                                    errorMsg={{
                                        required: data?.corroLink?.Verify?.Mobile?.Validation?.Required,
                                        length: data?.corroLink?.Verify?.Mobile?.Validation?.Length,
                                        prefix: data?.corroLink?.Verify?.Mobile?.Validation?.Prefix
                                    }}
                                />
                            </GelFormField>

                            <ErrorBox />
                            <GelFormField style={{ paddingTop: sizeBaseUnit * 4 }}>
                                <GelButton
                                    name=""
                                    primary
                                    large
                                    submit
                                    style={{ width: "100%" }}
                                >
                                    Continue
                                </GelButton>
                            </GelFormField>
                        </GelRowLayout>
                    </GelForm>
                )}

                {stage === "enterCode" && (
                    <GelForm
                        {...commonFormProps}
                        disableOnSubmit
                        onSubmit={verifyCustomer}
                        reset={codeForm.resetForm}
                        onReset={codeForm.onFormDataReset}
                    >
                        <GelRowLayout gutter="medium" style={{ paddingLeft: sizeBaseUnit * 3 }}>
                            <GelHeading3>
                                {data?.corroLink?.Verify?.OTP?.Heading}
                            </GelHeading3>
                            <GelParagraph style={{ paddingTop: sizeBaseUnit * 2 }}>
                                {interpolateString(data?.corroLink?.Verify?.OTP?.SubHeading, recipient)}
                            </GelParagraph>

                            <GelFormField label="Verification code">
                                <GelTextInput
                                    name="verificationCode"
                                    onChange={codeForm.onFormDataChange}
                                    value={codeForm.formData.verificationCode}
                                    required
                                    requiredErrorMsg={data?.corroLink?.Verify?.OTP?.Validation?.Required}
                                />
                            </GelFormField>

                            <div style={{ paddingTop: sizeBaseUnit * 4 }}>
                                <GelLabel style={{ fontWeight: 100, paddingRight: sizeBaseX2 }}>
                                    Didn't receive a code?
                                    <GelLink style={{ marginLeft: 4 }} onClick={resendCode}>
                                        Resend code
                                    </GelLink>
                                </GelLabel>
                            </div>

                            <ErrorBox />

                            <GelFormField style={{ paddingTop: sizeBaseUnit * 4 }}>
                                <GelButton
                                    name=""
                                    primary
                                    large
                                    submit
                                    style={{ width: "100%" }}
                                    loading={buttonLoading}
                                >
                                    Continue
                                </GelButton>
                            </GelFormField>
                        </GelRowLayout>
                    </GelForm>
                )}

                {isLargerThanSmScreen(screen) && <FundBanner />}
            </GelBoxLayout>
        </GelContainerLite>
    );
};

export default VerifyCorroLinkIdentity;